import React, { useState } from 'react';
import {
  Mic, MicOff, Video, VideoOff, MessageCircle, Paperclip,
  BookOpen, FileText, PhoneOff, Maximize, Users, Aperture,
  FolderOpen, MonitorUp,
} from 'lucide-react';

// Palette de marque (cf. src/styles/theme.css)
const INK = '#0b1f17';
const GREEN = '#10b981';
const GREEN_SOFT = 'rgba(16,185,129,0.14)';
const GREEN_BORDER = 'rgba(16,185,129,0.55)';
const IDLE_ICON = 'rgba(233,250,241,0.88)';
const IDLE_LABEL = 'rgba(233,250,241,0.5)';

const VideoControls = ({
  isCameraOn,
  isMicOn,
  onToggleCamera,
  onToggleMicrophone,
  onLeaveChannel,
  participantCount = 0,
  connectionQuality = 'excellent',
  userType = 'eleve',
  activePanel = null,
  onTogglePanel,
  onOpenBibliotheque,
  backgroundOn = true,
  onToggleBackground,
  isScreenSharing = false,
  onToggleScreenShare,
  visible = true,
}) => {

  const [showRessources, setShowRessources] = useState(false);

  const qualityColor = {
    excellent: '#10b981', good: '#84cc16', fair: '#f59e0b', poor: '#ef4444',
  }[connectionQuality] || '#94a3b8';

  const qualityLabel = {
    excellent: 'Excellent', good: 'Bon', fair: 'Moyen', poor: 'Faible',
  }[connectionQuality] || connectionQuality;

  // Bouton icône + label, façon Zoom (rectangle arrondi, icône en trait)
  const Btn = ({ Icon, label, onClick, active, danger, disabled }) => {
    const iconColor = danger ? '#fff' : active ? GREEN : IDLE_ICON;
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={label}
        aria-label={label}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          minWidth: 60, padding: '7px 6px 5px',
          borderRadius: 12,
          background: danger ? '#dc2626' : active ? GREEN_SOFT : 'transparent',
          border: active && !danger ? `1px solid ${GREEN_BORDER}` : '1px solid transparent',
          color: iconColor,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          transition: 'background .15s ease, border-color .15s ease',
        }}
        onMouseEnter={(e) => { if (!danger && !active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
        onMouseLeave={(e) => { if (!danger && !active) e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon size={22} strokeWidth={1.9} color={iconColor} />
        <span style={{
          fontSize: 10.5,
          fontWeight: active ? 600 : 500,
          letterSpacing: '0.02em',
          color: danger ? '#fff' : active ? GREEN : IDLE_LABEL,
        }}>
          {label}
        </span>
      </button>
    );
  };

  // Groupe (famille) dans le menu Ressources
  const RessourceGroup = ({ label, children }) => (
    <div style={{ marginBottom: 4 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'rgba(233,250,241,0.4)', padding: '6px 10px 4px',
      }}>{label}</div>
      {children}
    </div>
  );

  // Élément cliquable du menu Ressources
  const RessourceItem = ({ Icon, label, hint, onClick }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '9px 10px', borderRadius: 9, border: 'none',
        background: 'transparent', color: IDLE_ICON, cursor: 'pointer', textAlign: 'left',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon size={18} strokeWidth={1.9} color={GREEN} />
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        {hint && <span style={{ fontSize: 10.5, color: IDLE_LABEL }}>{hint}</span>}
      </span>
    </button>
  );

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      height: 74,
      background: INK,
      backdropFilter: 'blur(16px)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 6, padding: '0 20px', zIndex: 1000,
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      // Auto-masquage façon Zoom : glisse vers le bas + s'estompe quand inactif
      transform: visible ? 'translateY(0)' : 'translateY(110%)',
      opacity: visible ? 1 : 0,
      transition: 'transform .3s ease, opacity .3s ease',
      pointerEvents: visible ? 'auto' : 'none',
    }}>

      {/* Gauche — qualité + participants */}
      <div style={{
        position: 'absolute', left: 20,
        display: 'flex', alignItems: 'center', gap: 12,
        color: 'rgba(233,250,241,0.5)', fontSize: 12,
      }}>
        <span style={{ color: qualityColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: qualityColor, display: 'inline-block' }} />
          {qualityLabel}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Users size={15} strokeWidth={2} /> {participantCount}
        </span>
      </div>

      {/* Centre — contrôles */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Btn
          Icon={isMicOn ? Mic : MicOff}
          label={isMicOn ? 'Micro' : 'Muet'}
          onClick={onToggleMicrophone}
          active={isMicOn}
        />
        <Btn
          Icon={isCameraOn ? Video : VideoOff}
          label={isCameraOn ? 'Caméra' : 'Off'}
          onClick={onToggleCamera}
          active={isCameraOn}
        />
        <Btn
          Icon={MessageCircle}
          label="Chat"
          onClick={() => onTogglePanel('chat')}
          active={activePanel === 'chat'}
        />
        {userType === 'prof' ? (
          <div style={{ position: 'relative' }}>
            <Btn
              Icon={FolderOpen}
              label="Ressources"
              onClick={() => setShowRessources(v => !v)}
              active={showRessources || activePanel === 'share' || activePanel === 'library'}
            />
            {showRessources && (
              <>
                {/* Overlay transparent : un clic ailleurs ferme le menu */}
                <div
                  onClick={() => setShowRessources(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 1500 }}
                />
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%',
                  transform: 'translateX(-50%)', zIndex: 1600,
                  width: 232, background: INK,
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.45)', padding: 8,
                }}>
                  <RessourceGroup label="Langue">
                    <RessourceItem Icon={BookOpen} label="Bibliothèque"
                      hint="Lettres, alphabet"
                      onClick={() => { onTogglePanel('library'); setShowRessources(false); }} />
                  </RessourceGroup>
                  <RessourceGroup label="Supports de cours">
                    <RessourceItem Icon={FileText} label="Mes supports"
                      onClick={() => { onOpenBibliotheque && onOpenBibliotheque(); setShowRessources(false); }} />
                    <RessourceItem Icon={Paperclip} label="Partager un fichier"
                      onClick={() => { onTogglePanel('share'); setShowRessources(false); }} />
                  </RessourceGroup>
                </div>
              </>
            )}
          </div>
        ) : (
          <Btn
            Icon={BookOpen}
            label="Biblio"
            onClick={() => onTogglePanel('library')}
            active={activePanel === 'library'}
          />
        )}
        {userType === 'prof' && onToggleScreenShare && (
          <Btn
            Icon={MonitorUp}
            label={isScreenSharing ? 'Arrêter' : 'Partager écran'}
            onClick={onToggleScreenShare}
            active={isScreenSharing}
          />
        )}
        {onToggleBackground && (
          <Btn
            Icon={Aperture}
            label="Flou"
            onClick={onToggleBackground}
            active={backgroundOn}
          />
        )}

        <div style={{ width: 1, height: 34, background: 'rgba(255,255,255,0.1)', margin: '0 6px' }} />

        <Btn
          Icon={PhoneOff}
          label="Fin"
          onClick={onLeaveChannel}
          danger
        />
      </div>

      {/* Droite — plein écran */}
      <div style={{ position: 'absolute', right: 20 }}>
        <button
          onClick={() => {
            if (document.fullscreenElement) document.exitFullscreen();
            else document.documentElement.requestFullscreen();
          }}
          title="Plein écran"
          aria-label="Plein écran"
          style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: IDLE_ICON, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Maximize size={17} strokeWidth={2} />
        </button>
      </div>

    </div>
  );
};

export default VideoControls;
